import React, { useContext, useEffect, useState } from "react";
import Settings from "./Settings";
import DifficultyRatingsModal from "./DifficultyRatingsModal";
import { useNavigate } from "react-router-dom";
import { GameContext } from "../../Context/GameContext";
import useEscapeRoomApi from "../../Hooks/useEscapeRoomApi";
import { Event, ProfileSettings, RoomStates } from "../../Types/Game";
import AdminShell from "../../Components/AdminShell/AdminShell";
import { roomLabels } from "../../Config/config";

/**
 * EventManagement
 * ---------------
 * Admin dashboard to list, select, create/edit Events and preview the attached profile’s rooms.
 * Behaviour:
 * - Loads all events and their attached profiles, with an accordion for room details.
 * - Allows selecting the current event, editing (opens Settings), creating, and deleting events.
 * - Reflects branding from the attached profile (Gemba / Actica / Ntegra).
 */

/** TYPES ----------------------------------------------------------- */
type Props = { admin: boolean };

type EventMeta = {
  event: Event;
  profile?: ProfileSettings | null;
};

/** COMPONENT ------------------------------------------------------- */
const EventManagement = ({ admin }: Props) => {
  /** CONTEXT & NAV ------------------------------------------------- */
  const navigate = useNavigate();
  const { eventName, updateEventName } = useContext(GameContext);

  /** API ----------------------------------------------------------- */
  const api = useEscapeRoomApi();

  /** STATE --------------------------------------------------------- */
  const [edittedEvent, setEdittedEvent] = useState<string | undefined>(undefined);
  const [eventMeta, setEventMeta] = useState<EventMeta[] | undefined>(undefined);
  const [loadedEvents, setLoadedEvents] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [openEventIds, setOpenEventIds] = useState<Set<string>>(new Set());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isDifficultyModalOpen, setIsDifficultyModalOpen] = useState<boolean>(false);

  /** EFFECTS ------------------------------------------------------- */
  // Initial load of events + attached profiles
  useEffect(() => {
    const reload = async () => {
      setLoadedEvents(false);
      try {
        const events = await api.getAllEvents();
        const safeEvents: Event[] = Array.isArray(events) ? events : [];

        const metas = await Promise.all(
          safeEvents.map(async (ev) => {
            try {
              const profile = await api.getProfileSettings(ev.id);
              return { event: ev, profile: profile ?? null } as EventMeta;
            } catch {
              return { event: ev, profile: null } as EventMeta;
            }
          })
        );

        setEventMeta(metas);
      } catch {
        setEventMeta([]);
      } finally {
        setLoadedEvents(true);
      }
    };

    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** HANDLERS ------------------------------------------------------ */
  const handleSelectEvent = (ev: Event) => {
    updateEventName(ev.id);
    navigate("/");
  };

  const handleEditEvent = (ev: Event) => {
    setEdittedEvent(ev.id);
    setIsEditing(true);
  };

  const toggleAccordion = (eventId: string) => {
    setOpenEventIds((prev) => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  };

  const openDeleteConfirm = (id: string) => setConfirmDeleteId(id);
  const closeDeleteConfirm = () => setConfirmDeleteId(null);

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await api.deleteEvent(confirmDeleteId);
      if (eventName === confirmDeleteId) updateEventName("");
      setFeedback(`Deleted event: ${confirmDeleteId}`);
      setConfirmDeleteId(null);

      // Refresh list after deletion
      setLoadedEvents(false);
      try {
        const events = await api.getAllEvents();
        const safeEvents: Event[] = Array.isArray(events) ? events : [];
        const metas = await Promise.all(
          safeEvents.map(async (ev) => {
            try {
              const profile = await api.getProfileSettings(ev.id);
              return { event: ev, profile: profile ?? null } as EventMeta;
            } catch {
              return { event: ev, profile: null } as EventMeta;
            }
          })
        );
        setEventMeta(metas);
      } catch {
        setEventMeta([]);
      } finally {
        setLoadedEvents(true);
      }
    } catch (err) {
      console.error(err);
      setFeedback("Sorry, deleting this event isn’t available right now.");
      setConfirmDeleteId(null);
    }
  };

  /** HELPERS ------------------------------------------------------- */
  /** Return ordered, active room labels from a RoomStates object. */
  const getSelectedRoomLabels = (rooms?: RoomStates): string[] => {
    if (!rooms) return [];
    return Object.entries(rooms)
      .filter(([, r]) => r && r.active)
      .sort(([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0))
      .map(([id]) => roomLabels[id] ?? id);
  };

  type BrandingKey = "gemba" | "actica" | "ntegra";

  /** Return checkboxes if 'true' */
  const isOn = (v?: boolean | null) => v === true;

  /** Pick the first value that can be coerced to a finite number */
  const pickNumber = (...vals: unknown[]): number | undefined => {
    const hit = vals.find((x) => x !== undefined && x !== null && isFinite(Number(x)));
    return hit === undefined ? undefined : Number(hit);
  };

  /** Loosely-typed profile shape covering legacy/alternate timer field names. */
  type TimerLike = {
    escapeRoomTime?: unknown;
    timerMinutes?: unknown;
    timeMinutes?: unknown;
  };

  /** Best-effort extraction + normalisation to minutes. */
  const getTimerMins = (p?: TimerLike | null): number | undefined => {
    if (!p) return undefined;
    const num = pickNumber(p.escapeRoomTime, p.timerMinutes, p.timeMinutes);
    if (num === undefined) return undefined;
    return num > 180 || num % 60 === 0 ? Math.round(num / 60) : num;
  };

  const timerLabel = (mins?: number) =>
    Number.isFinite(mins) ? `${mins} mins on the timer` : "";

  /** Extract canonical branding key from an attached profile. */
  const getBrandingKeyFromProfile = (p?: ProfileSettings | null): BrandingKey => {
    const b = (p as unknown as { branding?: BrandingKey })?.branding;
    return b === "actica" || b === "ntegra" ? b : "gemba";
  };

  /** Convert a branding key to human-readable label. */
  const getBrandingLabelFromKey = (k: BrandingKey) =>
    k === "actica" ? "Actica" : k === "ntegra" ? "Ntegra" : "Gemba";

  /** RENDER GUARDS ------------------------------------------------- */
  if (!loadedEvents) return null;

  /** RENDER -------------------------------------------------------- */
  return (
    <div className="escape-room">
      {!isEditing ? (
        <AdminShell
          title="Digital Escape Rooms"
          subtitle="Event Management"
          avatar="/assets/avatars/robot-events.png"
        >
          <div className="event-form">
            {!eventMeta || eventMeta.length === 0 ? (
              <p>
                <b>No Events Available</b>
              </p>
            ) : (
              eventMeta.map(({ event, profile }) => {
                const selectedLabels = getSelectedRoomLabels(profile?.rooms);
                const isCurrent = event.id === eventName;
                const accordionOpen = openEventIds.has(event.id);

                return (
                  <div key={event.id} className="event-select cursor-pointer">
                    <div className="event-header">
                      <button
                        className="btn-blue btn-icon-only accordion-btn"
                        onClick={() => toggleAccordion(event.id)}
                        aria-expanded={accordionOpen}
                        aria-controls={`rooms-${event.id}`}
                        title={accordionOpen ? "Hide details" : "Show details"}
                        style={{ padding: 10 }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          style={{
                            transform: accordionOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.18s ease",
                          }}
                        >
                          <path
                            d="M7 10l5 5 5-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <div className="event-title">
                        {(() => {
                          const k = getBrandingKeyFromProfile(profile);
                          return (
                            <span className={`branding-pill branding-pill--${k}`}>
                              {getBrandingLabelFromKey(k)}
                            </span>
                          );
                        })()}
                        {event.id}
                      </div>

                      <div className="event-actions">
                        {isCurrent && (
                          <span
                            className="current-event-pill--strict"
                            role="status"
                            aria-label={`Current event ${event.id}`}
                            title={`Current event: ${event.id}`}
                          >
                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                              <path
                                d="M5 13l4 4L19 7"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <span className="current-event-pill__text">Selected Event</span>
                          </span>
                        )}

                        <button onClick={() => handleSelectEvent(event)} className="btn-blue">
                          Select
                        </button>

                        {admin && (
                          <>
                            <button onClick={() => handleEditEvent(event)} className="btn-blue">
                              Edit
                            </button>

                            <button
                              className="btn-red btn-icon-only"
                              aria-label={`Delete event ${event.id}`}
                              title="Delete event"
                              onClick={() => openDeleteConfirm(event.id)}
                            >
                              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
                                <path
                                  d="M3 6h18M9 6v-2a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m-9 0l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14M10 11v7M14 11v7"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {accordionOpen && (
                      <div id={`rooms-${event.id}`} style={{ marginTop: 8 }}>
                        {selectedLabels.length > 0 ? (
                          <div
                            style={{
                              color: "#4a4a4a",
                              fontSize: 13,
                              display: "flex",
                              flexDirection: "column",
                              gap: 8,
                            }}
                          >
                            <div className="profile-summary">
                              {(() => {
                                const pStyle = { fontSize: "14px", margin: 0, padding: 0, display: "inline" as const };
                                const mins = getTimerMins(profile);

                                const items = [
                                  <p key="name" style={pStyle}>
                                    <strong>Profile:</strong> {profile?.profileName ?? "—"}
                                  </p>,
                                  isOn(profile?.schoolInput) && (
                                    <p key="school" style={pStyle}>School names enabled</p>
                                  ),
                                  isOn(profile?.singleRoom) && (
                                    <p key="single" style={pStyle}>Single-room mode enabled</p>
                                  ),
                                  Number.isFinite(mins) && (
                                    <p key="timer" style={pStyle}>{timerLabel(mins)}</p>
                                  ),
                                ].filter(Boolean) as React.ReactNode[];

                                return (
                                  <div
                                    className="profile-summary"
                                    style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}
                                  >
                                    {items.map((node, idx) => (
                                      <div key={idx} style={{ display: "inline-flex", alignItems: "center" }}>
                                        {idx > 0 && (
                                          <span aria-hidden="true" style={{ margin: "0 8px", color: "#ccc" }}>|</span>
                                        )}
                                        {node}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>

                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 12,
                                alignItems: "flex-start",
                              }}
                            >
                              {selectedLabels.map((label) => {
                                const id =
                                  Object.keys(roomLabels).find((k) => roomLabels[k] === label) ?? label;

                                let imgSrc: string | undefined;
                                try {
                                  // @ts-ignore dynamic import for icons
                                  imgSrc = require(`../../Assets/icon-${id}.png`);
                                } catch {
                                  imgSrc = undefined;
                                }

                                return (
                                  <div
                                    key={id}
                                    style={{
                                      width: 82,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 80,
                                        height: 80,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "#fff",
                                      }}
                                    >
                                      {imgSrc ? (
                                        <img
                                          src={imgSrc}
                                          alt={label}
                                          style={{ width: 80, height: 80, objectFit: "contain" }}
                                          onError={(e) => {
                                            (e.currentTarget as HTMLImageElement).style.display = "none";
                                          }}
                                        />
                                      ) : (
                                        <div style={{ width: 80, height: 80 }} />
                                      )}
                                    </div>
                                    <div style={{ fontSize: 12, color: "#333", textAlign: "center" }}>
                                      {label}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div style={{ marginTop: 6, color: "#777", fontSize: 13 }}>
                            <em>No rooms selected</em>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {admin && (
              <div className="button-rail-center">
                <button
                  className="btn-blue"
                  onClick={() => {
                    setIsEditing(true);
                    setEdittedEvent(undefined);
                  }}
                >
                  Create New Event
                </button>
                <button
                  className="btn-blue difficulty-ratings-trigger"
                  style={{ marginLeft: 8 }}
                  onClick={() => setIsDifficultyModalOpen(true)}
                >
                  Adjust difficulty ratings
                </button>
              </div>
            )}

            {isDifficultyModalOpen && (
              <DifficultyRatingsModal onClose={() => setIsDifficultyModalOpen(false)} />
            )}

            {confirmDeleteId && (
              <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="deleteTitle">
                <div className="modal-card">
                  <h3 id="deleteTitle">Delete event?</h3>
                  <p>
                    Are you sure you want to delete <b>{confirmDeleteId}</b>? This action can’t be undone.
                  </p>
                  <div className="button-rail" style={{ justifyContent: "flex-end" }}>
                    <button className="btn-blue" onClick={handleConfirmDelete}>
                      Confirm
                    </button>
                    <button className="btn-grey" onClick={closeDeleteConfirm}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {feedback && <p className="feedback" style={{ marginTop: 8 }}>{feedback}</p>}
          </div>
        </AdminShell>
      ) : (
        <AdminShell
          title="Digital Escape Rooms"
          subtitle="Settings"
          avatar="/assets/avatars/robot-settings.png"
        >
          <Settings
            eventName={edittedEvent}
            admin={admin}
            onClose={async () => {
              setIsEditing(false);
              setLoadedEvents(false);
              try {
                const events = await api.getAllEvents();
                const safeEvents: Event[] = Array.isArray(events) ? events : [];
                const metas = await Promise.all(
                  safeEvents.map(async (ev) => {
                    try {
                      const profile = await api.getProfileSettings(ev.id);
                      return { event: ev, profile: profile ?? null } as EventMeta;
                    } catch {
                      return { event: ev, profile: null } as EventMeta;
                    }
                  })
                );
                setEventMeta(metas);
              } catch {
                setEventMeta([]);
              } finally {
                setLoadedEvents(true);
              }
            }}
          />
        </AdminShell>
      )}
    </div>
  );
};

export default EventManagement;